<template>
  <v-card
    border
    class="d-flex flex-column"
  >
    <v-card-title class="d-flex align-center justify-space-between pa-4">
      <h5>{{ $t('labels.filter-contacts') }}</h5>
      <v-btn
        v-if="canClose"
        icon="mdi-close"
        variant="text"
        @click="close"
      />
    </v-card-title>

    <v-card-text class="py-2 dialog-content-overflow">
      <v-expansion-panels
        v-model="panel"
        variant="accordion"
        flat
      >
        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.in-edit') }}
              <v-badge
                color="red"
                :model-value="isEditFilterActive"
                :content="editFilterActiveCount"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="filter-panel">
            <v-select
              v-model="filters.currentStatus"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter Status', value: $event })
              "
              :items="statusList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.status')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />
            <Feature :feature-slug="featureNames.ASSIGN_LEADS">
              <Permission permission="lead:assign">
                <v-select
                  v-model="filters.assignedUser"
                  :items="assignedUserList"
                  item-title="text"
                  item-value="value"
                  :label="$t('lead-list.filter-settings.labels.assigned-employee')"
                  hide-details
                  clearable
                  variant="outlined"
                  density="compact"
                />
              </Permission>
            </Feature>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel
          v-if="hasCrm"
          class="my-1 expansion-panel-border"
        >
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ crmContactStatusLabel }}
              <v-badge
                color="red"
                :model-value="isTagFilterActive"
                :content="tagFilterActiveCount"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="filter-panel">
            <TagFilter
              :initial-selected-tags="contactTags"
              :crm-name="crmName"
              @change="updateContactTypes"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.contact-actions') }}
              <v-badge
                color="red"
                :model-value="isConversionFilterActive"
                :content="conversionFilterActiveCount"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="filter-panel">
            <span class="text-body-2">{{
              $t('lead-list.filter-settings.labels.contact-display')
            }}</span>
            <div>
              <v-checkbox
                v-for="({ key, label, icon, type }, index) of conversionFilters"
                :key="key"
                v-model="filters[key]"
                @update:modelValue="
                  ($event) => trackEvent({ message: 'Filter Contact', value: $event ? type : null })
                "
                hide-details
                :class="{ 'mt-0': index === 0 }"
              >
                <template v-slot:label>
                  <div>
                    <v-icon size="small">
                      {{ icon }}
                    </v-icon>
                    {{ $t(label) }}
                  </div>
                </template>
              </v-checkbox>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <Feature :feature-slug="featureNames.POSTAL_SHIPPING">
          <v-expansion-panel class="my-1 expansion-panel-border">
            <v-expansion-panel-title class="px-2">
              <span class="text-body-1">
                {{ $t('lead-list.filter-settings.labels.send-option') }}
                <v-badge
                  color="red"
                  :model-value="filters.postalShipping"
                  content="1"
                  class="ml-4 mb-2"
                />
              </span>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="filter-panel">
              <span class="text-body-2">{{
                $t('lead-list.filter-settings.labels.contacts-with')
              }}</span>
              <v-checkbox
                v-model="filters.postalShipping"
                @update:modelValue="
                  ($event) => trackEvent({ message: 'Filter Postal Shipping', value: $event })
                "
                class="mt-0"
                hide-details
                :label="$t('lead-list.filter-settings.labels.postal-shipping')"
              />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </Feature>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.contact-type') }}
              <v-badge
                color="red"
                :model-value="isContactTypeFilterActive"
                content="1"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="py-0 filter-panel">
            <v-select
              v-model="filterContactType"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter Contact Type', value: $event })
              "
              :items="originList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.contact-type')"
              class="mb-4"
              hide-details
              variant="outlined"
              density="compact"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
          <v-expansion-panel class="my-1 expansion-panel-border">
            <v-expansion-panel-title class="px-2">
              <span class="text-body-1">
                {{ $t('lead-list.filter-settings.labels.acquisition-booster-status') }}
                <v-badge
                  color="red"
                  :model-value="isInvitationFilterActive"
                  content="1"
                  class="ml-4 mb-2"
                />
              </span>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="py-0 filter-panel">
              <v-select
                v-model="filters.invited"
                @update:modelValue="
                  ($event) => trackEvent({ message: 'Filter Invitation Status', value: $event })
                "
                :items="invitedLandingpageList"
                item-title="text"
                item-value="value"
                :label="$t('lead-list.filter-settings.labels.acquisition-booster-topic')"
                class="mb-4"
                hide-details
                clearable
                variant="outlined"
                density="compact"
              >
                <template v-slot:item="{ item, index, props }">
                  <template v-if="index === 2">
                    <span class="text-body-2 font-weight-bold pl-4 mt-2">
                      {{ $t('lead-list.filter-settings.labels.already-invited-to') }}
                    </span>
                  </template>
                  <v-list-item
                    v-bind="(item, index, props)"
                    :style="{ 'padding-left': index > 1 ? '20px !important' : '' }"
                  >
                    <span> {{ item.text }}</span>
                  </v-list-item>
                </template>
              </v-select>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </Feature>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.contact-sources') }}
              <v-badge
                color="red"
                :model-value="isSourceFilterActive"
                content="1"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="py-0 filter-panel">
            <v-select
              v-model="filters.source"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter Contact Source', value: $event })
              "
              :items="trafficSourceList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.contact-source')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.downloads') }}
              <v-badge
                color="red"
                :model-value="isDownloadLandingpageFilterActive"
                content="1"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="py-0 filter-panel">
            <v-select
              v-model="filters.downloadLandingpage"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter Download Landingpage', value: $event })
              "
              :items="downloadLandingpages"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.acquisition-booster-topic')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.due-date.title') }}
              <v-badge
                color="red"
                :model-value="isDueDateFilterActive"
                :content="dueDateFilterActiveCount"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="filter-panel">
            <div>
              <v-checkbox
                v-for="({ key, label }, index) of dueDates"
                :key="key"
                v-model="filters[key]"
                @update:modelValue="
                  ($event) =>
                    trackEvent({ message: 'Filter Due Date', value: $event ? label : null })
                "
                hide-details
                :class="{ 'mt-0': index === 0 }"
              >
                <template v-slot:label>
                  <div>
                    {{ $t(label) }}
                  </div>
                </template>
              </v-checkbox>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.zipcode') }}
              <v-badge
                color="red"
                :model-value="isZipcodeFilterActive"
                content="1"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="py-0 filter-panel">
            <v-select
              v-model="filters.zipcode"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter Zipcode', value: $event })
              "
              :items="zipcodeList"
              item-title="text"
              :label="$t('lead-list.filter-settings.labels.zipcode')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel class="my-1 expansion-panel-border">
          <v-expansion-panel-title class="px-2">
            <span class="text-body-1">
              {{ $t('lead-list.filter-settings.labels.utm-parameters') }}
              <v-badge
                color="red"
                :model-value="isUtmFilterActive"
                :content="utmFilterActiveCount"
                class="ml-4 mb-2"
              />
            </span>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="py-0 filter-panel">
            <v-select
              v-model="filters.utmSource"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter UTM Source', value: $event })
              "
              :items="utmSourceList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.utm-source')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />

            <v-select
              v-model="filters.utmMedium"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter UTM Medium', value: $event })
              "
              :items="utmMediumList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.utm-medium')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />

            <v-select
              v-model="filters.utmCampaign"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter UTM Campaign', value: $event })
              "
              :items="utmCampaignList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.utm-campaign')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />

            <v-select
              v-model="filters.utmTerm"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter UTM Term', value: $event })
              "
              :items="utmTermList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.utm-term')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />

            <v-select
              v-model="filters.utmContent"
              @update:modelValue="
                ($event) => trackEvent({ message: 'Filter UTM Content', value: $event })
              "
              :items="utmContentList"
              item-title="text"
              item-value="value"
              :label="$t('lead-list.filter-settings.labels.utm-content')"
              class="mb-4"
              hide-details
              clearable
              variant="outlined"
              density="compact"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <p class="mt-8 text-caption">
        {{ $t('lead-list.filter-settings.info') }}
      </p>
    </v-card-text>

    <v-spacer />

    <v-card-actions class="justify-end d-flex">
      <v-btn
        location="right"
        :disabled="activeFiltersCount <= 0"
        size="small"
        color="error"
        class="w-full"
        @click="resetFilters()"
      >
        {{ $t('buttons.lead-list.filter-settings.cta') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Permission from '@/components/Permission'
import { defaultSystemStatuses, statusForSelect as statusList } from '@/lib/leadStatus'
import { sortByString } from '@/lib/sort-functions'
import { TOOLS_WITH_DOWNLOADS } from '@/lib/tools'
import branding from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import { availableDueDates, dueDates } from '@/modules/leads/LeadList/lib/availableDueDates'
import { Feature } from '@/plugins/feature-config'

import { getActiveFilterCount, persistAllFilters, restoreAllFilters } from '../helper/filter'
import COMPANY from '../queries/Company.gql'
import GET_UTM_PARAMETERS from '../queries/GetUtmParameters.gql'
import LANDINGPAGE_SLUGS_USED_FOR_INVITATION from '../queries/LandingpageSlugsUsedForInvitation.gql'
import TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY from '../queries/TotalTrafficBySourceForCompany.gql'
import USERS from '../queries/Users.gql'
import ZIPCODES_FOR_COMPANY from '../queries/ZipcodesForCompany.gql'
import { originForSelect as originList } from './lib/leadOrigin'
import TagFilter from './TagFilter.vue'

export default {
  components: {
    TagFilter,
    Permission,
    Feature,
  },
  mixins: [featureMixin, branding],
  props: {
    hasTool: {
      type: Function,
      default: () => {},
    },
    canClose: {
      type: Boolean,
      default: true,
    },
    hasCrm: {
      type: Boolean,
      default: false,
    },
    crmName: {
      type: String,
      default: '',
    },
    leadsPipelineStatuses: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['filtersChanged', 'close'],
  data() {
    return {
      filters: {
        currentStatus: null,
        assignedUser: null,
        invited: null,
        source: null,
        downloadLandingpage: null,
        zipcode: null,
        leadOriginConversion: true,
        leadOriginImport: true,
        postalShipping: false,
        leadOriginCrm: false,
        utmSource: null,
        utmMedium: null,
        utmCampaign: null,
        utmTerm: null,
        utmContent: null,
        ...TOOLS_WITH_DOWNLOADS.reduce((obj, { key }) => ({ ...obj, [key]: false }), {}),
        ...availableDueDates.reduce((obj, key) => ({ ...obj, [key]: false }), {}),
        tags: [],
      },
      totalTrafficBySourceForCompany: [],
      utmParameters: {
        utmSource: [],
        utmMedium: [],
        utmCampaign: [],
        utmTerm: [],
        utmContent: [],
      },
      zipcodesForCompany: [],
      filterContactType: 'leadOriginAll',
      activeFiltersCount: 0,
      statusList: this.leadsPipelineStatuses.map((item) => {
        return this.mapStatusList(item.name)
      }),
      panel: -1,
    }
  },

  computed: {
    conversionFilters() {
      return TOOLS_WITH_DOWNLOADS.filter(({ type }) => this.hasTool(type))
    },
    availableDueDates() {
      return availableDueDates
    },
    dueDates() {
      return dueDates.map(({ key, label }) => ({
        key,
        label: this.$t(`lead-list.filter-settings.labels.due-date.${label}`),
      }))
    },
    contactTags() {
      return this.filters?.tags || []
    },
    editFilterActiveCount() {
      return [this.filters.currentStatus, this.filters.assignedUser].filter(Boolean).length
    },
    tagFilterActiveCount() {
      return this.filters?.tags?.length
    },
    dueDateFilterActiveCount() {
      const filters = {
        dueDateToday: this.filters.dueDateToday,
        withoutDueDate: this.filters.withoutDueDate,
        dueDateThisWeek: this.filters.dueDateThisWeek,
        dueDateThisMonth: this.filters.dueDateThisMonth,
        dueDateNextWeek: this.filters.dueDateNextWeek,
        dueDateNextMonth: this.filters.dueDateNextMonth,
        dueDateOverdue: this.filters.dueDateOverdue,
      }
      return Object.values(filters).reduce((count, filter) => count + (filter ? 1 : 0), 0)
    },
    conversionFilterActiveCount() {
      return [...this.conversionFilters.map(({ key }) => this.filters[key])].filter(Boolean).length
    },
    contactTypeFilterActiveCount() {
      return [
        this.filters.leadOriginConversion,
        this.filters.leadOriginImport,
        this.filters.leadOriginCrm,
      ].filter(Boolean).length
    },
    isConversionFilterActive() {
      return this.conversionFilterActiveCount > 0
    },
    isEditFilterActive() {
      return this.editFilterActiveCount > 0
    },
    isTagFilterActive() {
      return this.tagFilterActiveCount > 0
    },
    isContactTypeFilterActive() {
      return this.contactTypeFilterActiveCount === 1
    },
    isInvitationFilterActive() {
      return Boolean(this.filters.invited)
    },
    isSourceFilterActive() {
      return Boolean(this.filters.source)
    },
    isDownloadLandingpageFilterActive() {
      return Boolean(this.filters.downloadLandingpage)
    },
    isDueDateFilterActive() {
      return (
        this.filters.dueDateToday ||
        this.filters.withoutDueDate ||
        this.filters.dueDateThisWeek ||
        this.filters.dueDateThisMonth ||
        this.filters.dueDateNextWeek ||
        this.filters.dueDateNextMonth ||
        this.filters.dueDateOverdue
      )
    },
    isZipcodeFilterActive() {
      return Array.isArray(this.filters.zipcode)
        ? this.filters.zipcode.length > 0
        : Boolean(this.filters.zipcode)
    },

    isUtmFilterActive() {
      return Boolean(
        this.filters.utmSource ||
          this.filters.utmMedium ||
          this.filters.utmCampaign ||
          this.filters.utmTerm ||
          this.filters.utmContent
      )
    },

    utmFilterActiveCount() {
      return [
        this.filters.utmSource,
        this.filters.utmMedium,
        this.filters.utmCampaign,
        this.filters.utmTerm,
        this.filters.utmContent,
      ].filter(Boolean).length
    },

    utmSourceList() {
      return (this.utmParameters.utmSource || []).map(({ value }) => ({
        text: value,
        value,
      }))
    },

    utmMediumList() {
      return (this.utmParameters.utmMedium || []).map(({ value }) => ({
        text: value,
        value,
      }))
    },

    utmCampaignList() {
      return (this.utmParameters.utmCampaign || []).map(({ value }) => ({
        text: value,
        value,
      }))
    },

    utmTermList() {
      return (this.utmParameters.utmTerm || []).map(({ value }) => ({
        text: value,
        value,
      }))
    },

    utmContentList() {
      return (this.utmParameters.utmContent || []).map(({ value }) => ({
        text: value,
        value,
      }))
    },
    originList() {
      const list = originList
      const translatedList = list.map(({ value, text }) => ({
        value,
        text: this.$t(text),
      }))
      if (this.hasCrm) {
        translatedList.push({
          value: 'leadOriginCrm',
          text: this.$t('lead-list.lead-origin.import-crm', { crmName: this.crmName }),
        })
      }
      return translatedList
    },
    crmContactStatusLabel() {
      if (this.platform === 'remax')
        return this.$t('lead-list.filter-settings.labels.contact-type-crm')
      if (this.hasCrm) {
        const label = {
          onOffice: this.$t('lead-list.filter-settings.labels.contact-type-crm'),
          Propstack: this.$t('lead-list.filter-settings.labels.contact-status-crm'),
          Flowfact: this.$t('labels.tags'),
        }
        return this.crmName
          ? `${this.crmName} ${label[this.crmName] ?? this.$t('lead-list.filter-settings.labels.contact-status-crm')}`
          : this.$t('lead-list.filter-settings.labels.contact-status-crm')
      }
      return this.$t('lead-list.filter-settings.labels.contact-status-crm')
    },

    assignedUserList() {
      const assignedUserList = (this.users ?? [])
        .map((user) => ({
          value: user.id,
          text: `${user.firstname} ${user.lastname}`,
        }))
        .sort(sortByString('text'))

      return [
        {
          value: 'unassigned',
          text: this.$t('lead-list.filter-settings.labels.status-not-assigned'),
        },
        ...assignedUserList,
      ]
    },

    invitedLandingpageList() {
      const landingpageName = (slug) => {
        const landingpage = this.company?.landingpages?.find(
          ({ slug: landingpageSlug }) => landingpageSlug === slug
        )
        return landingpage?.name ?? slug
      }

      const invitedLandingapgeList = this.invitedLandingpageSlugs
        ?.map((slug) => ({
          value: slug,
          text: landingpageName(slug),
        }))
        .sort(sortByString('text'))

      return [
        {
          value: 'ALL',
          text: this.$t('lead-list.filter-settings.labels.all-invited'),
        },
        {
          value: 'NONE',
          text: this.$t('lead-list.filter-settings.labels.none-invited'),
        },
        ...invitedLandingapgeList,
      ]
    },

    downloadLandingpages() {
      return this.company?.landingpages
        ?.filter(({ type }) => type === 'GUIDE' || type === 'CHECKLIST')
        .map(({ name, slug }) => ({
          value: slug,
          text: name,
        }))
        .sort(sortByString('text'))
    },

    trafficSourceList() {
      return this.totalTrafficBySourceForCompany
        ?.filter(({ source }) => Boolean(source))
        ?.map(({ source }) => ({
          value: source,
          text: source,
        }))
        ?.sort(sortByString('text'))
    },

    zipcodeList() {
      const zipcodes = (this.zipcodesForCompany || [])
        .filter((zipcode) => zipcode && zipcode.trim() !== '')
        .map((zipcode) => ({
          value: zipcode,
          text: zipcode,
        }))
        .sort(sortByString('text'))

      return [
        {
          value: 'ALL_WITH_ZIPCODE',
          text: this.$t('lead-list.filter-settings.labels.zipcode-options.all-with-zipcode'),
        },
        {
          value: 'NO_ZIPCODE',
          text: this.$t('lead-list.filter-settings.labels.zipcode-options.no-zipcode'),
        },
        ...zipcodes,
      ]
    },
  },

  watch: {
    filters: {
      handler() {
        persistAllFilters({
          hasCrm: this.hasCrm,
          filters: this.filters,
          availableTools: this.conversionFilters,
          availableDueDates: this.availableDueDates,
        })
        this.activeFiltersCount = getActiveFilterCount({
          hasCrm: this.hasCrm,
          availableTools: this.conversionFilters,
          availableDueDates: this.availableDueDates,
        })
        this.$emit('filtersChanged', {
          filters: this.filters,
          activeFiltersCount: this.activeFiltersCount,
        })
        // Refetch zipcodes when filters change
        if (this.$apollo.queries.zipcodesForCompany) {
          this.$apollo.queries.zipcodesForCompany.refetch()
        }
      },
      deep: true,
    },
    filterContactType(newValue) {
      if (newValue === 'leadOriginAll') {
        this.filters.leadOriginConversion = true
        this.filters.leadOriginImport = true
        if (this.hasCrm) {
          this.filters.leadOriginCrm = true
        }
      } else if (newValue === 'leadOriginImport') {
        this.filters.leadOriginConversion = false
        this.filters.leadOriginImport = true
        if (this.hasCrm) {
          this.filters.leadOriginCrm = false
        }
      } else if (newValue === 'leadOriginCrm') {
        this.filters.leadOriginConversion = false
        this.filters.leadOriginImport = false
        this.filters.leadOriginCrm = true
      } else {
        this.filters.leadOriginConversion = true
        this.filters.leadOriginImport = false
        if (this.hasCrm) {
          this.filters.leadOriginCrm = false
        }
      }
    },
  },

  created() {
    if (this.hasCrm) {
      this.filters.leadOriginCrm = true
    }
    this.filters = restoreAllFilters({
      hasCrm: this.hasCrm,
      availableTools: this.conversionFilters,
      availableDueDates: this.availableDueDates,
    })
    this.activeFiltersCount = getActiveFilterCount({
      hasCrm: this.hasCrm,
      availableTools: this.conversionFilters,
      availableDueDates: this.availableDueDates,
    })
    this.filterContactType = this.mapFilterContactType()
  },

  mounted() {
    this.setInitialPanel()
    this.setFiltersFromQueryParams()
  },

  methods: {
    trackEvent({ message = '', value = '' } = {}) {
      if (value) this.$tracking.event('Leads', 'Selected', message, value)
    },
    mapStatusList(name) {
      if (defaultSystemStatuses.includes(name)) {
        const matchedStatus = Object.values(statusList).find((status) => status.value === name)

        if (matchedStatus) {
          return {
            value: matchedStatus.value,
            text: this.$t(matchedStatus.text),
          }
        }
      }

      return { value: name, text: name }
    },
    setInitialPanel() {
      if (Object.keys(this.$route.query).includes('filter')) {
        this.panel = 1
      }
    },
    mapFilterContactType() {
      if (this.filters.leadOriginImport && this.filters.leadOriginConversion) return 'leadOriginAll'
      if (this.filters.leadOriginImport) return 'leadOriginImport'
      if (this.filters.leadOriginCrm) return 'leadOriginCrm'
      return 'leadOriginConversion'
    },
    close() {
      this.$emit('close')
      if (Object.keys(this.$route.query).includes('filter')) {
        this.$router.push('/leads')
      }
    },
    async updateContactTypes(selectedTags) {
      this.filters.tags = selectedTags
      if (selectedTags?.length)
        this.trackEvent({ message: 'Filter Contact Tags', value: selectedTags.join(', ') })
    },
    setFiltersFromQueryParams() {
      const { filter, ...queryParams } = this.$route.query || {}
      const allowedParams = ['valuations', 'propertyRequests', 'returnCalls', 'new']

      if (filter && allowedParams.includes(filter)) {
        if (filter === 'new') {
          this.filters.currentStatus = 'new'
        } else {
          this.filters[filter] = true
        }
      }

      const utmParams = ['utmSource', 'utmMedium', 'utmCampaign', 'utmTerm', 'utmContent']
      utmParams.forEach((param) => {
        if (queryParams[param]) {
          this.filters[param] = queryParams[param]
        }
      })
    },
    resetFilters() {
      this.filters.currentStatus = null
      this.filters.assignedUser = null
      this.filters.invited = null
      this.filters.source = null
      this.filters.downloadLandingpage = null
      this.filters.zipcode = null
      this.filters.utmSource = null
      this.filters.utmMedium = null
      this.filters.utmCampaign = null
      this.filters.utmTerm = null
      this.filters.utmContent = null
      this.filters.leadOriginConversion = true
      this.filters.leadOriginImport = true
      if (this.hasCrm) {
        this.filters.leadOriginCrm = true
        this.filters.tags = [] // Direct assignment instead of $set
      }
      this.filters.postalShipping = false
      this.conversionFilters.forEach(({ key }) => {
        this.filters[key] = false
      })
      this.availableDueDates.forEach((key) => {
        this.filters[key] = false
      })
      this.filterContactType = this.mapFilterContactType()
      const { filter } = this.$route.query || {}
      if (filter) this.$router.push({ path: '/leads', replace: true })
    },
  },
  apollo: {
    company: {
      query: COMPANY,
      variables() {
        return {
          companyId: this.$auth.user.companyId,
        }
      },
    },
    invitedLandingpageSlugs: {
      query: LANDINGPAGE_SLUGS_USED_FOR_INVITATION,
    },
    users: {
      query: USERS,
      variables() {
        return {
          input: {
            companyId: this.$auth.user.companyId,
          },
        }
      },
    },
    totalTrafficBySourceForCompany: {
      query: TOTAL_TRAFFIC_BY_SOURCE_FOR_COMPANY,
    },
    zipcodesForCompany: {
      query: ZIPCODES_FOR_COMPANY,
      variables() {
        return {
          input: {
            filters: {
              currentStatus: this.filters.currentStatus,
              assignedUser: this.filters.assignedUser,
              leadOriginImport: this.filters.leadOriginImport,
              leadOriginConversion: this.filters.leadOriginConversion,
              leadOriginCrm: this.filters.leadOriginCrm,
              returnCalls: this.filters.returnCalls,
              downloads: this.filters.downloads,
              valuations: this.filters.valuations,
              lifeAnnuities: this.filters.lifeAnnuities,
              partialSales: this.filters.partialSales,
              plotValuations: this.filters.plotValuations,
              propertyRequests: this.filters.propertyRequests,
              tags: this.filters.tags,
            },
          },
        }
      },
      fetchPolicy: 'no-cache',
    },
    utmParameters: {
      query: GET_UTM_PARAMETERS,
    },
  },
}
</script>

<style scoped>
.dialog-content-overflow {
  max-height: 100%;
  overflow: scroll;
}
.gap {
  gap: 10px;
}
.filter-width {
  width: 240px;
}
</style>
<style>
.filter-panel > .v-expansion-panel-content__wrap {
  padding-left: 7px;
  padding-right: 7px;
  padding-top: 0px;
  padding-bottom: 10px;
}
</style>
